<template>
  <div class="billing-page-container">
    <!-- Hero Header Banner matching PaymentStatsItem -->
    <div class="stats-hero">
      <div class="hero-content">
        <div class="hero-icon-badge">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
            <rect x="2" y="5" width="20" height="14" rx="2"/>
            <line x1="2" y1="10" x2="22" y2="10"/>
          </svg>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">{{ isStaff ? 'Kredit Alapú Elszámolás & Egyenleg' : 'Pénzügyi Állapot' }}</h1>
          <p class="hero-subtitle">{{ isStaff ? 'Táncosi egyenleg, havi díjtervek, kedvezmények és pénzügyi tranzakciós előzmények' : 'Fizetési státusz és egyenleg információ' }}</p>
        </div>
      </div>

      <!-- Child Selector Dropdown -->
      <div v-if="childrenList.length > 0" class="hero-child-select-wrap">
        <label for="child-select" class="hero-select-label">Táncos Váltása:</label>
        <select id="child-select" v-model="activeChildId" @change="onChildChange" class="hero-select">
          <option v-for="c in childrenList" :key="c.id" :value="c.id">
            {{ c.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Active Child Info Bar Card -->
    <div v-if="activeChildName" class="active-dancer-card mb-4">
      <div class="dancer-profile-head">
        <div class="dancer-avatar-badge">
          {{ getInitials(activeChildName) }}
        </div>
        <div>
          <h2 class="dancer-main-name">{{ activeChildName }}</h2>
          <span class="dancer-id-pill">ID: #{{ activeChildId }}</span>
        </div>
      </div>

      <div class="dancer-groups-wrap">
        <span v-if="!activeChildGroups.length" class="no-groups-pill">Nincs csoport csatolva</span>
        <span v-for="g in activeChildGroups" :key="g.group_id || g.id" :class="['group-pill-tag', { 'primary': g.is_primary }]">
          {{ g.group_name || g.name }} <small v-if="g.is_primary">(Elsődleges)</small>
        </span>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger rounded-4 shadow-sm mb-4">{{ error }}</div>

    <!-- Wallet Balance Card Dashboard (Staff View) -->
    <div v-if="isStaff" class="wallet-dashboard-card mb-4" :class="{ 'in-debt': hasCarriedDebt || (wallet && wallet.balance < 0) }">
      <div class="wallet-main-info">
        <span class="wallet-badge-label">Havi Befizetés & Egyenleg ({{ formatMonthLabel(selectedMonth) }})</span>
        <div class="wallet-amount-row">
          <span class="wallet-amount-val">{{ formatPrice(monthlyPaid) }}</span>
          <span class="wallet-curr-tag">Befizetve ebben a hónapban</span>
        </div>
        <div class="d-flex align-items-center gap-2 flex-wrap mt-2">
          <div class="wallet-status-tag" :class="isPaidForMonth ? 'ok' : (monthlyPaid > 0 ? 'partial' : 'debt')">
            <span v-if="isPaidForMonth">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="me-1"><polyline points="20 6 9 17 4 12"/></svg>
              Havi Díj Rendezve
            </span>
            <span v-else-if="monthlyPaid > 0">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="me-1"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Részlegesen Befizetve ({{ formatPrice(monthlyPaid) }} / {{ formatPrice(fullMonthlyTotal) }})
            </span>
            <span v-else>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="me-1"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
              Nincs Befizetve erre a hónapra
            </span>
          </div>

          <span v-if="hasCarriedDebt" class="badge bg-danger text-white px-2.5 py-1 rounded-pill font-weight-bold d-inline-flex align-items-center gap-1">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/></svg>
            Áthozott tartozás: {{ formatPrice(effectiveCarriedDebt) }}
          </span>
        </div>

        <div class="mt-2.5 text-muted small d-flex align-items-center gap-2 flex-wrap">
          <span v-if="hasCarriedDebt">Áthozott tartozás: <strong class="text-danger">{{ formatPrice(effectiveCarriedDebt) }}</strong></span>
          <span v-else-if="wallet && wallet.balance < 0">Fennálló tartozás: <strong class="text-danger">{{ formatPrice(Math.abs(wallet.balance)) }}</strong></span>
          <span v-else>Egyenleg: <strong>0 Ft</strong></span>
          <span>•</span>
          <span class="text-secondary" v-if="resetScheduleDesc">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="me-0.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            Havi nullázás: {{ resetScheduleDesc }}
          </span>
        </div>
      </div>

      <div class="wallet-actions-group" v-if="canCredit">
        <button class="wallet-btn topup-btn" @click="openTopUp">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Egyenleg Feltöltése
        </button>
        <button class="wallet-btn correct-btn" @click="openCorrect">Korrekció</button>
        <button class="wallet-btn refund-btn" @click="openRefund">Visszatérítés</button>
      </div>
    </div>

    <!-- Parent & Dancer Status Card (Simplified view: only see if paid or still owe money and how much) -->
    <div v-else class="wallet-dashboard-card mb-4" :class="{ 'in-debt': hasCarriedDebt, 'in-good-standing': !hasCarriedDebt && isPaidForMonth }">
      <div class="wallet-main-info">
        <span class="wallet-badge-label">Fizetési Státusz ({{ formatMonthLabel(selectedMonth) }})</span>
        <div v-if="hasCarriedDebt">
          <div class="d-flex align-items-center gap-2 mb-2">
            <span class="badge bg-danger text-white fs-6 px-3 py-1.5 rounded-pill font-weight-bold d-inline-flex align-items-center gap-1.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Fennálló tartozás
            </span>
          </div>
          <div class="wallet-amount-row mt-2">
            <span class="text-muted fs-6 me-2">Összes fizetendő tétel:</span>
            <span class="wallet-amount-val text-danger">{{ formatPrice(effectiveTotalDue) }}</span>
          </div>
          <p class="text-muted small mt-2 mb-0">
            Havi tagdíj ({{ formatPrice(fullMonthlyTotal) }}) + korábbi hónapokból áthozott tartozás ({{ formatPrice(effectiveCarriedDebt) }}). Kérjük, rendezze a hátralékot az oktatónál!
          </p>
        </div>
        <div v-else-if="!isPaidForMonth">
          <div class="d-flex align-items-center gap-2 mb-2">
            <span class="badge bg-warning text-dark fs-6 px-3 py-1.5 rounded-pill font-weight-bold d-inline-flex align-items-center gap-1.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {{ monthlyPaid > 0 ? 'Részlegesen befizetve' : 'Befizetésre vár' }}
            </span>
          </div>
          <div class="wallet-amount-row mt-2">
            <span class="text-muted fs-6 me-2">Fizetendő havi tagdíj:</span>
            <span class="wallet-amount-val text-warning">{{ formatPrice(Math.max(0, fullMonthlyTotal - monthlyPaid)) }}</span>
          </div>
          <p class="text-muted small mt-2 mb-0">
            {{ monthlyPaid > 0 ? `Eddig befizetve: ${formatPrice(monthlyPaid)} / ${formatPrice(fullMonthlyTotal)}.` : `Kérjük, rendezze a(z) ${formatMonthLabel(selectedMonth)} havi tagdíjat az oktatónál.` }}
          </p>
        </div>
        <div v-else>
          <div class="d-flex align-items-center gap-2 mb-2">
            <span class="badge bg-success text-white fs-6 px-3 py-1.5 rounded-pill font-weight-bold d-inline-flex align-items-center gap-1.5">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              Minden fizetve az aktuális hónapra
            </span>
          </div>
          <div class="wallet-amount-row mt-2">
            <span class="text-muted fs-6 me-2">Fennálló tartozás:</span>
            <span class="wallet-amount-val text-success">0 Ft</span>
          </div>
          <p class="text-muted small mt-2 mb-0">
            A(z) {{ formatMonthLabel(selectedMonth) }} havi tagdíj rendezve van ({{ formatPrice(monthlyPaid) }}), a fiókban nincs tartozás.
          </p>
        </div>
      </div>
    </div>

    <!-- Section 1: Monthly Calculation Card (Staff only) -->
    <div v-if="isStaff" class="panel-card mb-4">
      <div class="panel-header mb-3">
        <div class="panel-title-wrap">
          <div class="panel-icon bg-blue-subtle text-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div>
            <h2 class="panel-title">Havi Számla és Kalkuláció</h2>
            <p class="panel-subtitle">Jelenlegi hónapra kiszámított tagdíj, kedvezmények és távollét-csökkentések</p>
          </div>
        </div>

        <div class="month-picker-wrap">
          <input type="month" v-model="selectedMonth" @change="onMonthChange" class="modern-month-input" />
        </div>
      </div>

      <!-- Main Focus: Monthly Payment Status Hero Card -->
      <div class="monthly-status-hero-card mb-3" :class="isPaidForMonth ? 'status-paid' : (monthlyPaid > 0 ? 'status-partial' : 'status-unpaid')">
        <div class="status-hero-left">
          <div class="status-hero-icon-box">
            <svg v-if="isPaidForMonth" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            <svg v-else-if="monthlyPaid > 0" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <svg v-else width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          </div>
          <div>
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <span class="status-hero-title">
                {{ isPaidForMonth ? 'Fizetve az aktuális hónapra' : (monthlyPaid > 0 ? 'Részlegesen befizetve' : 'Nincs befizetve erre a hónapra') }}
              </span>
              <span class="status-month-badge">{{ formatMonthLabel(selectedMonth) }}</span>
            </div>
            <p class="status-hero-desc mb-0">
              <template v-if="isPaidForMonth && !hasCarriedDebt">
                A táncos rendezte a teljes havi tagdíjat. Befizetve: <strong>{{ formatPrice(monthlyPaid) }}</strong> / {{ formatPrice(fullMonthlyTotal) }}
              </template>
              <template v-else-if="isPaidForMonth && hasCarriedDebt">
                A havi díj rendezve van, de korábbi hónapokból még <strong>fennáll {{ formatPrice(effectiveCarriedDebt) }} tartozás (fentmaradás)</strong>. Mindösszesen fizetendő: <strong class="text-danger">{{ formatPrice(effectiveTotalDue) }}</strong>
              </template>
              <template v-else-if="monthlyPaid > 0">
                A havi díjból eddig befizetve: <strong>{{ formatPrice(monthlyPaid) }}</strong> / {{ formatPrice(fullMonthlyTotal) }}. Fennmaradó havi díj: <strong class="text-danger">{{ formatPrice(Math.max(0, fullMonthlyTotal - monthlyPaid)) }}</strong>
                <span v-if="hasCarriedDebt"> • Áthozott tartozással együtt összesen: <strong class="text-danger">{{ formatPrice(effectiveTotalDue) }}</strong></span>
              </template>
              <template v-else>
                Erre a hónapra még nem érkezett tagdíj-befizetés (0 Ft). Fizetendő havi díj: <strong class="text-danger">{{ formatPrice(fullMonthlyTotal) }}</strong>
                <span v-if="hasCarriedDebt"> + korábbi hónapokból áthozott tartozás (fentmaradás): <strong class="text-danger">{{ formatPrice(effectiveCarriedDebt) }}</strong> (mindösszesen: <strong class="text-danger">{{ formatPrice(effectiveTotalDue) }}</strong>)</span>
              </template>
            </p>
          </div>
        </div>

        <div class="status-hero-right text-end">
          <div class="status-paid-amount-label">Befizetve ebben a hónapban:</div>
          <div class="status-paid-amount-val" :class="isPaidForMonth ? 'text-success' : (monthlyPaid > 0 ? 'text-warning' : 'text-danger')">
            {{ formatPrice(monthlyPaid) }}
          </div>
          <div class="reset-schedule-hint" v-if="resetScheduleDesc">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="me-1"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            {{ resetScheduleDesc }}
          </div>
        </div>
      </div>

      <!-- Carried-Over Debt Warning Banner (DO NOT ZERO OUT DEBT) -->
      <div v-if="hasCarriedDebt" class="carried-debt-banner mb-3 p-3 rounded-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3">
          <div class="carried-debt-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/>
              <line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div>
            <div class="d-flex align-items-center gap-2">
              <span class="badge bg-danger text-white rounded-pill px-2.5 py-1 font-weight-bold">Áthozott korábbi tartozás</span>
              <span class="text-muted small">Korábbi hónapokból fennmaradt hátralék (fentmaradás, ami továbbgördül!)</span>
            </div>
            <div class="mt-1 small text-dark">
              Fennálló tartozás az előző hónapokból: <strong class="text-danger fs-6">{{ formatPrice(effectiveCarriedDebt) }}</strong>
              <span class="ms-2 text-muted">• Teljes fizetendő tétel a havi díjjal együtt: <strong>{{ formatPrice(effectiveTotalDue) }}</strong></span>
            </div>
          </div>
        </div>
        <button v-if="canCredit" class="btn btn-sm btn-danger rounded-pill px-3 font-weight-bold" @click="openTopUp">
          Tartozás rendezése &rarr;
        </button>
      </div>

      <!-- Staff detailed breakdown -->
      <div v-if="calculation" class="calc-breakdown-card mb-3">
        <!-- Main Focus: Status Badge inside breakdown -->
        <div class="calc-row-item status-focus-item">
          <span class="calc-label font-weight-bold">Havi Státusz:</span>
          <span v-if="isPaidForMonth" class="calc-status-badge badge-paid">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            Fizetve ({{ formatPrice(monthlyPaid) }})
          </span>
          <span v-else-if="monthlyPaid > 0" class="calc-status-badge badge-partial">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Részleges ({{ formatPrice(monthlyPaid) }})
          </span>
          <span v-else class="calc-status-badge badge-unpaid">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            Nincs fizetve (0 Ft)
          </span>
        </div>
        <!-- Reduced size for Teljes havi alapdíj -->
        <div class="calc-row-item base reduced-base" v-if="fullMonthlyBase">
          <span class="calc-label">Teljes havi alapdíj:</span>
          <span class="calc-val text-muted font-weight-bold">{{ formatPrice(fullMonthlyBase) }}</span>
        </div>
        <div class="calc-row-item discount" v-if="calcDiscount > 0">
          <span class="calc-label">Kedvezmények összesen:</span>
          <span class="calc-val text-emerald font-weight-bold">-{{ formatPrice(calcDiscount) }}</span>
        </div>
        <div class="calc-row-item absence" v-if="calcAbsence > 0">
          <span class="calc-label">Távollét miatti csökkentés:</span>
          <span class="calc-val text-emerald font-weight-bold">-{{ formatPrice(calcAbsence) }}</span>
        </div>
        <div class="calc-row-item total">
          <span class="calc-label font-weight-bold">{{ hasOnlyClassPlans ? 'Fizetendő Óradíj:' : 'Fizetendő Havi Díj:' }}</span>
          <span class="calc-val total-val text-blue">{{ formatPrice(fullMonthlyTotal) }}</span>
        </div>
        <div class="calc-row-item debt-row text-danger" v-if="hasCarriedDebt">
          <span class="calc-label text-danger font-weight-bold">Áthozott korábbi tartozás (fentmaradás):</span>
          <span class="calc-val text-danger font-weight-bold">+{{ formatPrice(effectiveCarriedDebt) }}</span>
        </div>
        <div class="calc-row-item total total-with-debt border-top pt-2" v-if="hasCarriedDebt">
          <span class="calc-label font-weight-bold">Összes fizetendő tétel mindennel együtt:</span>
          <span class="calc-val total-val text-danger">{{ formatPrice(effectiveTotalDue) }}</span>
        </div>
      </div>

      <!-- Prorated Details Toggle Button for Admins -->
      <div v-if="calculation && hasMonthlyPlansWithLessons" class="mb-3">
        <button 
          type="button" 
          class="btn-prorated-toggle d-inline-flex align-items-center gap-2"
          @click="showProratedDetails = !showProratedDetails"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>{{ showProratedDetails ? 'Időarányos részletek elrejtése' : 'Részletek: Eddig megtartott órák arányos díja' }}</span>
          <span v-if="proratedLessonsBadgeText" class="prorated-lessons-badge ms-1">
            {{ proratedLessonsBadgeText }}
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" :style="{ transform: showProratedDetails ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        <!-- Prorated Detail Collapsible Card -->
        <div v-if="showProratedDetails" class="prorated-details-panel p-3.5 rounded-4 mt-2.5 border">
          <div class="prorated-header d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2 pb-2">
            <div class="d-flex align-items-center gap-2">
              <span class="prorated-admin-badge">
                Részletes kalkuláció adminoknak
              </span>
              <span class="text-muted small">Időarányos összegzés a hónapban</span>
            </div>
            <div class="text-end">
              <span class="text-muted small me-2">Eddigi arányos végösszeg:</span>
              <strong class="text-primary fs-5">{{ formatPrice(calculation.total) }}</strong>
            </div>
          </div>

          <div class="prorated-expl-box mb-3 p-3 rounded-3 d-flex align-items-start gap-2.5">
            <div class="prorated-expl-text">
              <strong class="d-block mb-1">Hogyan kell ezt értelmezni?</strong>
              Ez az összeg (<strong class="text-primary">{{ formatPrice(calculation.total) }}</strong>) a hónapban <strong>eddig ténylegesen megtartott órák</strong> ({{ proratedLessonsFraction }} óra) alapján számított időarányos díj. 
              Az adminisztráció és a szülők számára a mérvadó fizetendő tétel a fenti <strong>teljes havi tagdíj ({{ formatPrice(fullMonthlyTotal) }})</strong> vagy az óránkénti díj.
            </div>
          </div>

          <div class="prorated-plans-list">
            <div v-for="cp in monthlyPlansWithLessons" :key="'prorated-' + cp.plan.id" class="prorated-plan-row p-3 rounded-3 mb-2">
              <div class="d-flex justify-content-between align-items-center mb-1.5">
                <span class="prorated-group-name">{{ cp.plan.group_name }}</span>
                <span class="prorated-plan-amount">{{ formatPrice(cp.monthly_total) }}</span>
              </div>
              <div class="d-flex flex-wrap gap-3 small prorated-meta">
                <span>Megtartott órák: <strong>{{ cp.finished_lessons != null ? cp.finished_lessons : cp.attended }} / {{ cp.lessons_in_month }} óra</strong></span>
                <span>Számított óradíj: <strong>{{ formatPrice(cp.per_lesson) }} / óra</strong></span>
                <span>Képlet: {{ cp.finished_lessons != null ? cp.finished_lessons : cp.attended }} óra × {{ formatPrice(cp.per_lesson) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Discount Leader Status Banner (Staff only) -->
      <div v-if="overridePrice != null" class="discount-leader-card p-3 rounded-4 mb-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div class="d-flex align-items-center gap-3">
            <div class="discount-leader-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                <line x1="19" y1="5" x2="5" y2="19"/>
                <circle cx="6.5" cy="6.5" r="2.5"/>
                <circle cx="17.5" cy="17.5" r="2.5"/>
              </svg>
            </div>
            <div>
              <div class="d-flex align-items-center gap-2">
                <span class="discount-status-badge">
                  Egyedi kedvezmény aktív
                </span>
                <span class="text-muted small">Forrás: Beállítások &rarr; Kedvezmények</span>
              </div>
              <div class="mt-1">
                <span class="discount-effective-label font-weight-bold">Hatályos havi díj:</span>
                <strong class="text-success ms-1 fs-5">{{ formatPrice(overridePrice) }}</strong>
                <span v-if="fullMonthlyBase > overridePrice" class="text-muted ms-2 small">
                  (Megtakarítás: -{{ formatPrice(fullMonthlyBase - overridePrice) }})
                </span>
              </div>
            </div>
          </div>
          <router-link v-if="canWrite" to="/settings/discounts" class="btn-discount-link">
            Kedvezmények módosítása &rarr;
          </router-link>
        </div>
      </div>
      <div v-else-if="calculation" class="discount-leader-card default-mode p-3 rounded-4 mb-3">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div class="text-muted small">
            Nincs egyedi kedvezmény rögzítve.
          </div>
          <router-link v-if="canWrite" to="/settings/discounts" class="btn-discount-link-subtle">
            Egyedi díj beállítása &rarr;
          </router-link>
        </div>
      </div>

      <div v-for="cp in calculation ? calculation.plans : []" :key="cp.plan.id" class="plan-calc-box mb-2">
        <div class="plan-calc-head">
          <span class="font-weight-bold text-dark me-2">{{ cp.plan.group_name }}</span>
          <span class="mode-tag-pill" :class="cp.plan.billing_mode === 'monthly' ? 'mode-monthly' : 'mode-hourly'">
            {{ cp.plan.billing_mode === 'monthly' ? 'Havi fix díj' : 'Óránkénti díj' }}
          </span>
          <div class="ms-auto text-end">
            <span v-if="cp.plan.billing_mode === 'monthly'" class="font-weight-bold text-blue fs-6">
              {{ formatPrice(getPlanFullAmount(cp)) }}
            </span>
            <span v-else class="font-weight-bold text-blue fs-6">
              {{ formatPrice(cp.catalog_base) }} / óra
            </span>
            <div class="text-muted text-uppercase" style="font-size: 0.68rem; font-weight: 600;">
              {{ cp.plan.billing_mode === 'monthly' ? 'Teljes havi díj' : 'Óránkénti ár' }}
            </div>
          </div>
        </div>
        <div class="plan-calc-body text-muted small mt-1">
          <template v-if="showProratedDetails && cp.plan.billing_mode === 'monthly' && cp.lessons_in_month > 0">
            <div class="p-2 rounded-2 bg-light border mt-2 text-dark">
              <span class="badge bg-secondary-subtle text-secondary me-1">Időarányos díj eddig:</span>
              <strong class="text-primary">{{ formatPrice(cp.monthly_total) }}</strong>
              <span class="ms-2 text-muted">(Megtartott: {{ cp.finished_lessons != null ? cp.finished_lessons : cp.attended }}/{{ cp.lessons_in_month }} óra • Óránként: {{ formatPrice(cp.per_lesson) }})</span>
            </div>
          </template>

          <div v-if="cp.discounts && cp.discounts.length > 0" class="mt-1">
            <span v-for="d in cp.discounts" :key="d.rule_id" class="badge bg-success-subtle text-success border px-2 py-0.5 me-1">
              {{ d.rule_name }}: -{{ formatPrice(d.applied) }}
            </span>
          </div>
          <div v-if="cp.absence_reduction > 0" class="mt-1">
            <span class="badge bg-info-subtle text-info border px-2 py-0.5">
              Távollét: -{{ formatPrice(cp.absence_reduction) }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Section 2: Group Catalog Prices (Staff only) -->
    <div v-if="isStaff" class="panel-card mb-4">
      <div class="panel-header mb-3">
        <div class="panel-title-wrap">
          <div class="panel-icon bg-indigo-subtle text-indigo">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div>
            <h2 class="panel-title">Csoport Alapárak Katalógusa</h2>
            <p class="panel-subtitle">Egyes csoportok óradíjai és havi díjai</p>
          </div>
        </div>
      </div>

      <div v-if="displayedGroupPrices.length > 0" class="table-container">
        <table class="modern-table">
          <thead>
            <tr><th class="ps-4">Csoport Neve</th><th>Óra Ára</th><th class="text-end pe-4">Havi Díj</th></tr>
          </thead>
          <tbody>
            <tr v-for="gp in displayedGroupPrices" :key="gp.group_id">
              <td class="ps-4 font-weight-bold text-dark">{{ groupName(gp.group_id) }}</td>
              <td class="text-muted font-weight-medium">{{ formatPrice(gp.price_per_class) }}</td>
              <td class="text-end pe-4 font-weight-bold text-dark">{{ formatPrice(gp.price_monthly) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="empty-state py-3 text-center">
        Még nincs beállítva csoport ár.
      </div>
    </div>

    <!-- Section 3: Billing Plans per Group (Staff only) -->
    <div v-if="isStaff" class="panel-card mb-4">
      <div class="panel-header mb-3">
        <div class="panel-title-wrap">
          <div class="panel-icon bg-blue-subtle text-blue">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
          </div>
          <div>
            <h2 class="panel-title">Díjtervek Csoportonként</h2>
            <p class="panel-subtitle">Táncoshoz rendelt egyéni és csoportos elszámolási tervek</p>
          </div>
        </div>

        <button v-if="canWrite" class="btn btn-sm btn-primary rounded-pill px-3 font-weight-bold" @click="openPlanModal">+ Új Díjterv</button>
      </div>

      <div v-if="plans.length > 0" class="plans-compact-grid">
        <div v-for="plan in plans" :key="plan.id" class="plan-compact-item">
          <div class="plan-info-main">
            <span class="plan-name text-truncate" :title="plan.group_name">{{ plan.group_name }}</span>
            <span class="mode-tag-pill" :class="plan.billing_mode === 'monthly' ? 'mode-monthly' : 'mode-hourly'">
              {{ plan.billing_mode === 'monthly' ? 'Havi' : 'Óra' }}
            </span>
            <span v-if="!plan.is_active" class="badge bg-secondary text-white px-1.5 py-0.5 small">Inaktív</span>
          </div>

          <div class="plan-compact-actions" v-if="canWrite">
            <button class="btn-plan-action btn-plan-edit" @click="editPlan(plan)" title="Szerkesztés">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              <span>Szerkesztés</span>
            </button>
            <button class="btn-plan-action btn-plan-delete" @click="removePlan(plan.id)" title="Törlés">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
              <span>Törlés</span>
            </button>
          </div>
        </div>
      </div>
      <div v-else class="empty-state py-3 text-center">Még nincs díjterv rögzítve.</div>
    </div>

    <!-- Section 5: Absences (Staff only) -->
    <div v-if="isStaff" class="panel-card mb-4">
      <div class="panel-header mb-3">
        <div class="panel-title-wrap">
          <div class="panel-icon bg-indigo-subtle text-indigo">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
          </div>
          <div>
            <h2 class="panel-title">Távollétek & Kimaradások</h2>
            <p class="panel-subtitle">Előre bejelentett utazások és hiányzások díjcsökkentései</p>
          </div>
        </div>

        <button v-if="canAbsence" class="btn btn-sm btn-primary rounded-pill px-3 font-weight-bold" @click="openAbsenceModal">+ Új Távollét</button>
      </div>

      <div v-for="a in absences" :key="a.id" class="absence-row-item p-3 rounded-3 d-flex justify-content-between align-items-center mb-2">
        <div>
          <strong class="absence-dates">{{ a.start_date }} &rarr; {{ a.end_date }}</strong>
          <span class="text-muted small ms-2"> ({{ absenceKindText(a.reduction_kind) }}: {{ formatPrice(a.reduction_value) }})</span>
        </div>
        <div v-if="canAbsence">
          <button class="btn btn-sm btn-outline-danger rounded-pill px-3" @click="removeAbsence(a.id)">Törlés</button>
        </div>
      </div>
      <div v-if="absences.length === 0" class="empty-state py-3 text-center">Nincs rögzített távollét.</div>
    </div>

    <!-- Section 6: Ledger Transactions Table (Staff only) -->
    <div v-if="isStaff" class="panel-card mb-4">
      <div class="panel-header mb-3">
        <div class="panel-title-wrap">
          <div class="panel-icon bg-emerald-subtle text-emerald">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="12 1 12 23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
          </div>
          <div>
            <h2 class="panel-title">Egyenlegmozgások & Tranzakciós Előzmények</h2>
            <p class="panel-subtitle">Feltöltések, levonások, korrekciók és visszatérítések naplója</p>
          </div>
        </div>
      </div>

      <div class="table-container" v-if="ledger && ledger.length > 0">
        <table class="modern-table">
          <thead>
            <tr>
              <th class="ps-4">Dátum</th>
              <th>Típus</th>
              <th>Összeg</th>
              <th>Egyenleg Utána</th>
              <th>Indok / Ok</th>
              <th>Megjegyzés</th>
              <th class="text-end pe-4">Részletek</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="e in ledger" :key="e.id" class="ledger-table-row" @click="openTxnDetails(e)" style="cursor: pointer;">
              <td class="ps-4 text-muted small">{{ formatDateTime(e.created_at) }}</td>
              <td>
                <span class="badge" :class="getTxnBadgeClass(e.txn_type)">{{ txnText(e.txn_type) }}</span>
                <span v-if="isLessonDeduction(e)" class="badge bg-warning-subtle text-warning border ms-1 small" style="font-size: 0.7rem;">
                  Óradíj
                </span>
              </td>
              <td class="font-weight-bold" :class="e.amount < 0 ? 'text-rose' : 'text-emerald'">
                {{ formatPrice(e.amount) }}
                <div v-if="isLessonDeduction(e)" class="text-muted font-weight-normal" style="font-size: 0.72rem;">
                  1 óra levonás
                </div>
              </td>
              <td class="font-weight-bold text-dark">{{ e.balance_after ? formatPrice(e.balance_after) : '-' }}</td>
              <td class="text-muted small">{{ e.reason || '-' }}</td>
              <td class="text-muted small">{{ e.note || '-' }}</td>
              <td class="text-end pe-4">
                <button type="button" class="btn btn-sm btn-outline-secondary rounded-pill px-2.5 py-0.5" style="font-size: 0.75rem;" @click.stop="openTxnDetails(e)">
                  Részletek
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="empty-state py-4 text-center">Nincs rögzített tranzakció.</div>
    </div>

    <!-- Modals (Plan, Absence, Wallet) -->
    <!-- Plan modal -->
    <div v-if="showPlanModal" class="modal-backdrop-overlay" @click.self="showPlanModal = false">
      <div class="modal-card">
        <div class="modal-head">
          <h3>{{ editingPlan ? 'Díjterv Szerkesztése' : 'Új Díjterv' }}</h3>
          <button class="close-x" @click="showPlanModal = false">&times;</button>
        </div>
        <div class="modal-body-content">
          <div class="mb-3" v-if="!editingPlan">
            <label class="form-label font-weight-bold">Csoport</label>
            <select v-model="planForm.group_id" class="form-select modern-select">
              <option v-for="g in unassignedGroups" :key="g.id" :value="g.id">{{ g.name }}</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label font-weight-bold">Elszámolás Módja</label>
            <select v-model="planForm.billing_mode" class="form-select modern-select">
              <option value="monthly">Havi fix díj</option>
              <option value="class">Óránkénti díj</option>
            </select>
          </div>
          <div class="d-flex justify-content-end gap-2 mt-4">
            <button class="btn btn-sm btn-secondary rounded-pill px-4" @click="showPlanModal = false">Mégse</button>
            <button class="btn btn-sm btn-primary rounded-pill px-4" @click="submitPlan">Mentés</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Absence modal -->
    <div v-if="showAbsenceModal" class="modal-backdrop-overlay" @click.self="showAbsenceModal = false">
      <div class="modal-card">
        <div class="modal-head">
          <h3>Új Távollét Rögzítése</h3>
          <button class="close-x" @click="showAbsenceModal = false">&times;</button>
        </div>
        <div class="modal-body-content">
          <div class="mb-3">
            <label class="form-label font-weight-bold">Kezdő Dátum</label>
            <input type="date" v-model="absenceForm.start_date" class="form-control modern-input" />
          </div>
          <div class="mb-3">
            <label class="form-label font-weight-bold">Záró Dátum</label>
            <input type="date" v-model="absenceForm.end_date" class="form-control modern-input" />
          </div>
          <div class="mb-3">
            <label class="form-label font-weight-bold">Csökkentés Típusa</label>
            <select v-model="absenceForm.reduction_kind" class="form-select modern-select">
              <option value="proportional">Arányos (óra alapján)</option>
              <option value="fixed">Fix összeghatár (HUF)</option>
              <option value="percent">Százalékos (%)</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label font-weight-bold">Érték (Fix HUF / % / arány)</label>
            <input type="number" step="0.01" v-model.number="absenceForm.reduction_value" class="form-control modern-input" />
          </div>
          <div class="d-flex justify-content-end gap-2 mt-4">
            <button class="btn btn-sm btn-secondary rounded-pill px-4" @click="showAbsenceModal = false">Mégse</button>
            <button class="btn btn-sm btn-primary rounded-pill px-4" @click="submitAbsence">Mentés</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Wallet modal -->
    <div v-if="showWalletModal" class="modal-backdrop-overlay" @click.self="showWalletModal = false">
      <div class="modal-card" style="max-width: 500px; width: 92%;">
        <div class="modal-head">
          <h3>{{ walletAction.title }}</h3>
          <button class="close-x" @click="showWalletModal = false">&times;</button>
        </div>
        <div class="modal-body-content">
          <!-- Double Check Dancer Verification Card -->
          <div class="verify-dancer-card p-3 mb-3 rounded-3">
            <div class="d-flex align-items-center justify-content-between mb-1.5">
              <span class="verify-dancer-title">
                Táncos ellenőrzése (Dupla ellenőrzés)
              </span>
              <span class="verify-dancer-id">ID: #{{ activeChildId }}</span>
            </div>
            <div class="d-flex align-items-center gap-2.5 mt-1">
              <div class="verify-dancer-avatar">
                {{ getInitials(activeChildName) }}
              </div>
              <div>
                <div class="verify-dancer-name">
                  {{ activeChildName }}
                </div>
                <small class="text-muted">
                  <template v-if="hasCarriedDebt">
                    Áthozott tartozás: <strong class="text-danger">{{ formatPrice(effectiveCarriedDebt) }}</strong>
                  </template>
                  <template v-else-if="wallet && wallet.balance < 0">
                    Fennálló tartozás: <strong class="text-danger">{{ formatPrice(Math.abs(wallet.balance)) }}</strong>
                  </template>
                  <template v-else>
                    Jelenlegi egyenleg: <strong class="text-muted">0 Ft</strong>
                  </template>
                </small>
              </div>
            </div>
          </div>

          <!-- Group selection / verification dropdown (for topup) -->
          <div class="mb-3" v-if="walletAction.type === 'topup'">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <label class="form-label font-weight-bold small text-uppercase mb-0">Csoport / Osztály (Befizetéshez):</label>
              <span v-if="activeChildGroups.length" class="badge-group-selected" style="font-size: 0.75rem;">
                {{ activeChildGroups.map(g => g.name || g.group_name).join(', ') }}
              </span>
            </div>
            <select v-model.number="walletForm.group_id" class="form-select modern-select">
              <option :value="0">Nincs csoportba beosztva</option>
              <option v-for="g in groups" :key="g.id" :value="g.id">
                {{ g.name }}
              </option>
            </select>
            <small class="text-muted mt-1 d-block" style="font-size: 0.75rem;">
              Ha a táncos nem ebbe a csoportba fizet be, itt közvetlenül átállíthatod.
            </small>
          </div>

          <div class="mb-3">
            <label class="form-label font-weight-bold">Összeg (HUF)</label>
            <input type="number" v-model.number="walletForm.amount" class="form-control modern-input" placeholder="pl. 10000" />
          </div>
          <div class="mb-3" v-if="walletAction.type === 'topup'">
            <label class="form-label font-weight-bold">Fizetési Mód</label>
            <select v-model="walletForm.method" class="form-select modern-select">
              <option value="készpénz">Készpénz</option>
              <option value="banki átutalás">Banki átutalás</option>
              <option value="bankkártya">Bankkártya</option>
            </select>
          </div>
          <div class="mb-3">
            <label class="form-label font-weight-bold">Megjegyzés / Indoklás</label>
            <input type="text" v-model="walletForm.note" class="form-control modern-input" placeholder="Opcionális megjegyzés..." />
          </div>
          <div class="d-flex justify-content-end gap-2 mt-4">
            <button class="btn btn-sm btn-secondary rounded-pill px-4" @click="showWalletModal = false">Mégse</button>
            <button class="btn btn-sm btn-primary rounded-pill px-4" @click="submitWalletAction">Mentés</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Transaction Detail Modal for Admins -->
    <div v-if="showTxnModal && selectedTxn" class="modal-backdrop-overlay" @click.self="showTxnModal = false">
      <div class="modal-card" style="max-width: 480px; width: 92%;">
        <div class="modal-head">
          <div class="d-flex align-items-center gap-2">
            <div class="rounded-circle d-flex align-items-center justify-content-center" :class="selectedTxn.amount < 0 ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'" style="width: 34px; height: 34px;">
              <svg v-if="selectedTxn.amount < 0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="18 15 12 9 6 15"/></svg>
            </div>
            <h3 class="mb-0">Tranzakció Részletei</h3>
          </div>
          <button class="close-x" @click="showTxnModal = false">&times;</button>
        </div>
        <div class="modal-body-content">
          <!-- Summary card -->
          <div class="txn-summary-box p-3 mb-3 rounded-3">
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span class="badge" :class="getTxnBadgeClass(selectedTxn.txn_type)">{{ txnText(selectedTxn.txn_type) }}</span>
              <span class="text-muted small">{{ formatDateTime(selectedTxn.created_at) }}</span>
            </div>
            <div class="d-flex align-items-baseline justify-content-between">
              <span class="text-muted small">Összeg:</span>
              <span class="fs-4 font-weight-bold" :class="selectedTxn.amount < 0 ? 'text-rose' : 'text-emerald'">
                {{ formatPrice(selectedTxn.amount) }}
              </span>
            </div>
            <div class="d-flex align-items-baseline justify-content-between mt-1 pt-1 border-top">
              <span class="text-muted small">Egyenleg a művelet után:</span>
              <span class="font-weight-bold txn-val-text">{{ selectedTxn.balance_after ? formatPrice(selectedTxn.balance_after) : '-' }}</span>
            </div>
          </div>

          <!-- Explanation box for Admins -->
          <div class="txn-explanation-box small py-2.5 px-3 rounded-3 mb-3">
            <div class="txn-explanation-title">Magyarázat az adminoknak:</div>
            <p class="mb-0 mt-1">{{ getTxnExplanation(selectedTxn) }}</p>
          </div>

          <!-- Key value details -->
          <div class="small">
            <div class="d-flex justify-content-between py-1.5 border-bottom" v-if="selectedTxn.note">
              <span class="text-muted">Megjegyzés / Óra:</span>
              <span class="font-weight-medium txn-val-text text-end">{{ selectedTxn.note }}</span>
            </div>
            <div class="d-flex justify-content-between py-1.5 border-bottom" v-if="selectedTxn.reason">
              <span class="text-muted">Indok kategória:</span>
              <span class="font-weight-medium txn-val-text">{{ selectedTxn.reason }}</span>
            </div>
            <div class="d-flex justify-content-between py-1.5 border-bottom">
              <span class="text-muted">Tranzakció ID:</span>
              <span class="text-muted">#{{ selectedTxn.id }}</span>
            </div>
            <div class="d-flex justify-content-between py-1.5" v-if="selectedTxn.billing_month">
              <span class="text-muted">Vonatkozó elszámolási hónap:</span>
              <span class="txn-val-text font-weight-medium">{{ selectedTxn.billing_month }}</span>
            </div>
          </div>

          <div class="d-flex justify-content-end mt-4">
            <button class="btn btn-sm btn-secondary rounded-pill px-4" @click="showTxnModal = false">Bezárás</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'BillingItem',
  props: {
    childId: { type: [Number, String], default: null }
  },
  data() {
    return {
      activeChildId: null,
      childrenList: [],
      wallet: null,
      ledger: [],
      plans: [],
      absences: [],
      groups: [],
      groupPrices: [],
      calculation: null,
      selectedMonth: this.currentMonth(),
      error: '',
      userRole: null,
      canWrite: false,
      canCredit: false,
      canAbsence: false,
      childInfo: null,
      overridePrice: null,
      showPlanModal: false,
      editingPlan: null,
      planForm: { group_id: null, billing_mode: 'monthly', note: '', is_active: true },
      showAbsenceModal: false,
      absenceForm: { start_date: '', end_date: '', reduction_kind: 'proportional', reduction_value: 0.5, reason: '' },
      showWalletModal: false,
      walletAction: { type: 'topup', title: '' },
      showProratedDetails: false,
      selectedTxn: null,
      showTxnModal: false,
      pollHandle: null,
      monthlyPaid: 0,
      monthlyFeePaid: false,
      monthlyStatus: 'unpaid',
      carriedDebt: 0,
      totalDue: 0,
      resetScheduleDesc: '',
      visualBalance: 0
    };
  },
  computed: {
    effectiveCarriedDebt() {
      if (this.carriedDebt > 0) return this.carriedDebt;
      if (this.wallet && this.wallet.balance < 0) return Math.abs(this.wallet.balance);
      return 0;
    },
    hasCarriedDebt() {
      return this.effectiveCarriedDebt > 0;
    },
    isPaidForMonth() {
      if (this.monthlyFeePaid && !this.hasCarriedDebt) return true;
      if (this.fullMonthlyTotal > 0 && this.monthlyPaid >= (this.fullMonthlyTotal + this.effectiveCarriedDebt)) return true;
      if (this.fullMonthlyTotal === 0 && !this.hasCarriedDebt) return true;
      return false;
    },
    effectiveTotalDue() {
      if (this.totalDue !== undefined && this.totalDue !== null) {
        if (this.totalDue === 0 && (this.monthlyPaid > 0 || (this.fullMonthlyTotal === 0 && !this.hasCarriedDebt))) {
          return 0;
        }
        if (this.totalDue > 0) {
          return this.totalDue;
        }
      }
      const remainingMonthly = Math.max(0, this.fullMonthlyTotal - this.monthlyPaid);
      const remainingCarried = Math.max(0, this.effectiveCarriedDebt - Math.max(0, this.monthlyPaid - this.fullMonthlyTotal));
      return remainingMonthly + remainingCarried;
    },
    isStaff() {
      return this.userRole !== null ? this.userRole >= 700 : (this.canWrite || this.canAbsence);
    },
    displayedGroupPrices() {
      if (this.isStaff) {
        return this.groupPrices;
      }
      const childGroupIds = new Set();
      if (this.childInfo) {
        if (Array.isArray(this.childInfo.groups)) {
          this.childInfo.groups.forEach(g => {
            const gid = g.id || g.group_id;
            if (gid) childGroupIds.add(Number(gid));
          });
        }
        if (this.childInfo.group_id) {
          childGroupIds.add(Number(this.childInfo.group_id));
        }
      }
      if (Array.isArray(this.plans)) {
        this.plans.forEach(p => {
          if (p.group_id) childGroupIds.add(Number(p.group_id));
        });
      }
      if (childGroupIds.size > 0) {
        return this.groupPrices.filter(gp => childGroupIds.has(Number(gp.group_id)));
      }
      return this.groupPrices;
    },
    activeChildName() {
      const c = (this.childrenList || []).find(item => item.id === this.activeChildId);
      if (c) return c.name;
      if (this.childInfo && (this.childInfo.sure_name || this.childInfo.surename || this.childInfo.family_name || this.childInfo.familyname)) {
        return `${this.childInfo.sure_name || this.childInfo.surename || ''} ${this.childInfo.family_name || this.childInfo.familyname || ''}`.trim();
      }
      return '';
    },
    activeChildGroups() {
      if (!this.childInfo) return [];
      if (Array.isArray(this.childInfo.groups) && this.childInfo.groups.length) {
        return this.childInfo.groups;
      }
      if (this.childInfo.group_name) {
        return [{ id: this.childInfo.group_id, name: this.childInfo.group_name, is_primary: true }];
      }
      return [];
    },
    activePlanCount() {
      return (this.plans || []).filter(p => p.is_active).length;
    },
    hasMultipleGroups() {
      return this.activePlanCount > 1;
    },
    unassignedGroups() {
      const assignedIds = new Set((this.plans || []).map(p => p.group_id));
      return (this.groups || []).filter(g => !assignedIds.has(g.id));
    },
    calcBase() {
      if (!this.calculation || !Array.isArray(this.calculation.plans)) return 0;
      return this.calculation.plans.reduce((s, p) => s + (p.base_price || 0), 0);
    },
    fullMonthlyBase() {
      if (this.overridePrice != null) return this.overridePrice;
      if (!this.calculation || !Array.isArray(this.calculation.plans)) return 0;
      return this.calculation.plans.reduce((s, p) => {
        const base = (p.catalog_base > 0) ? p.catalog_base : (p.base_price || 0);
        return s + base;
      }, 0);
    },
    fullMonthlyTotal() {
      if (this.overridePrice != null) {
        return Math.max(0, this.overridePrice - this.calcAbsence);
      }
      return Math.max(0, this.fullMonthlyBase - this.calcDiscount - this.calcAbsence);
    },
    hasMonthlyPlansWithLessons() {
      if (!this.calculation || !Array.isArray(this.calculation.plans)) return false;
      return this.calculation.plans.some(
        p => p.plan && p.plan.billing_mode === 'monthly' && (p.lessons_in_month > 0 || (p.finished_lessons || p.attended) > 0)
      );
    },
    monthlyPlansWithLessons() {
      if (!this.calculation || !Array.isArray(this.calculation.plans)) return [];
      return this.calculation.plans.filter(
        p => p.plan && p.plan.billing_mode === 'monthly'
      );
    },
    proratedLessonsBadgeText() {
      if (!this.calculation || !Array.isArray(this.calculation.plans)) return '';
      const mPlans = this.calculation.plans.filter(p => p.plan && p.plan.billing_mode === 'monthly' && p.lessons_in_month > 0);
      if (!mPlans.length) return '';
      const finished = mPlans.reduce((s, p) => s + (p.finished_lessons != null ? p.finished_lessons : (p.attended || 0)), 0);
      const total = mPlans.reduce((s, p) => s + (p.lessons_in_month || 0), 0);
      return `${finished}/${total} óra megtartva`;
    },
    proratedLessonsFraction() {
      if (!this.calculation || !Array.isArray(this.calculation.plans)) return '';
      const mPlans = this.calculation.plans.filter(p => p.plan && p.plan.billing_mode === 'monthly' && p.lessons_in_month > 0);
      if (!mPlans.length) return '';
      const finished = mPlans.reduce((s, p) => s + (p.finished_lessons != null ? p.finished_lessons : (p.attended || 0)), 0);
      const total = mPlans.reduce((s, p) => s + (p.lessons_in_month || 0), 0);
      return `${finished}/${total}`;
    },
    hasOnlyClassPlans() {
      if (!this.calculation || !Array.isArray(this.calculation.plans) || !this.calculation.plans.length) return false;
      return this.calculation.plans.every(p => p.plan && p.plan.billing_mode === 'class');
    },
    calcDiscount() {
      if (!this.calculation || !Array.isArray(this.calculation.plans)) return 0;
      return this.calculation.plans.reduce((s, p) => {
        const dSum = (p.discounts || []).reduce((acc, d) => acc + (d.applied || 0), 0);
        return s + dSum;
      }, 0);
    },
    calcAbsence() {
      if (!this.calculation || !Array.isArray(this.calculation.plans)) return 0;
      return this.calculation.plans.reduce((s, p) => s + (p.absence_reduction || 0), 0);
    }
  },
  watch: {
    childId: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.activeChildId = Number(newVal);
          this.loadAll();
          this.fetchCatalog();
        }
      }
    }
  },
  mounted() {
    this.checkPermissions();
    this.fetchChildrenList();
    this.fetchCatalog();
  },
  methods: {
    currentMonth() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      return `${y}-${m}`;
    },
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    async checkPermissions() {
      try {
        const host = this.$BACKEND_HOST || '';
        const res = await axios.get(host + '/api/session');
        const user = res.data;
        const role = user ? user.role : 0;
        this.userRole = role;
        this.canWrite = role >= 800;
        this.canCredit = role >= 800;
        this.canAbsence = role >= 700;
      } catch (e) {
        this.userRole = 0;
        this.canWrite = false;
        this.canCredit = false;
        this.canAbsence = false;
      }
    },
    async fetchChildrenList() {
      try {
        const host = this.$BACKEND_HOST || '';
        const res = await axios.get(host + '/api/settings/children/info');
        const list = res.data.children || (Array.isArray(res.data) ? res.data : []);
        this.childrenList = list.map(c => ({
          id: c.id,
          name: (c.name || `${c.sure_name || c.surename || ''} ${c.family_name || c.familyname || ''}`).trim() || `Táncos #${c.id}`
        }));
      } catch (e) {
        this.childrenList = [];
      }

      // If user is parent/dancer or non-admin, /api/settings/children/info returns 403.
      // Fall back to session children from store / auth handler
      if (!this.childrenList || this.childrenList.length === 0) {
        let sessionChildren = [];
        if (typeof this.$getChildId === 'function') {
          sessionChildren = this.$getChildId() || [];
        } else if (this.$store && this.$store.getters && this.$store.getters.childId) {
          sessionChildren = this.$store.getters.childId || [];
        }
        if (!Array.isArray(sessionChildren) || sessionChildren.length === 0) {
          try {
            const cached = JSON.parse(localStorage.getItem('childId'));
            if (Array.isArray(cached)) {
              sessionChildren = cached;
            }
          } catch (err) {}
        }
        if (Array.isArray(sessionChildren) && sessionChildren.length > 0) {
          this.childrenList = sessionChildren.map(c => ({
            id: c.id || c.ID,
            name: (c.fullName || c.full_name || c.name || `${c.sure_name || ''} ${c.family_name || ''}`).trim() || `Táncos #${c.id || c.ID}`
          }));
        }
      }

      const rawPropId = this.childId || (this.$route && this.$route.params && (this.$route.params.id || this.$route.params.childId));
      const propId = Number(rawPropId);
      if (propId && !isNaN(propId)) {
        this.activeChildId = propId;
      } else if (this.childrenList.length > 0 && !this.activeChildId) {
        this.activeChildId = this.childrenList[0].id;
      } else if (!this.activeChildId) {
        const uid = this.$getUserId ? this.$getUserId() : null;
        if (uid) {
          this.activeChildId = Number(uid);
        }
      }

      if (this.activeChildId) {
        this.loadAll();
        this.fetchCatalog();
      }
    },
    async fetchCatalog() {
      const host = this.$BACKEND_HOST || '';
      try {
        const gRes = await axios.get(host + '/api/settings/groups/info');
        this.groups = gRes.data.groups || (Array.isArray(gRes.data) ? gRes.data : []);
      } catch (e) {
        console.error('Error fetching groups:', e);
      }
      try {
        const url = host + '/api/billing/group-prices' + (this.activeChildId ? `?child_id=${this.activeChildId}` : '');
        const pRes = await axios.get(url);
        this.groupPrices = pRes.data.prices || (Array.isArray(pRes.data) ? pRes.data : []);
      } catch (e) {
        console.error('Error fetching group prices:', e);
      }
    },
    onChildChange() {
      if (this.activeChildId) {
        this.loadAll();
        this.fetchCatalog();
        if (this.$route && this.$route.params && this.$route.params.id != this.activeChildId) {
          this.$router.push(`/children/${this.activeChildId}/billing`).catch(() => {});
        }
      }
    },
    async loadAll() {
      if (!this.activeChildId) return;
      this.error = '';
      await Promise.all([
        this.loadChildInfo(),
        this.loadWallet(),
        this.loadLedger(),
        this.loadPlans(),
        this.loadAbsences(),
        this.loadCalculation(),
        this.loadOverride()
      ]);
    },
    async loadOverride() {
      if (!this.activeChildId) return;
      try {
        const host = this.$BACKEND_HOST || '';
        const res = await axios.get(host + `/api/billing/override/${this.activeChildId}`);
        if (res.data && res.data.override !== undefined) {
          this.overridePrice = res.data.override;
        }
      } catch (e) {
        // quiet fallback
      }
    },
    async loadChildInfo() {
      try {
        const host = this.$BACKEND_HOST || '';
        const res = await axios.get(host + `/api/child/${this.activeChildId}`);
        this.childInfo = res.data.child || res.data || {};
      } catch (e) {
        console.error('Error loading child info:', e);
      }
    },
    async loadWallet() {
      try {
        const host = this.$BACKEND_HOST || '';
        const res = await axios.get(host + `/api/billing/wallet/${this.activeChildId}`, {
          params: { month: this.selectedMonth }
        });
        this.wallet = res.data.wallet || res.data;
        if (res.data) {
          if (res.data.monthly_paid !== undefined) this.monthlyPaid = res.data.monthly_paid;
          if (res.data.monthly_fee_paid !== undefined) this.monthlyFeePaid = res.data.monthly_fee_paid;
          if (res.data.monthly_status !== undefined) this.monthlyStatus = res.data.monthly_status;
          if (res.data.carried_debt !== undefined) this.carriedDebt = res.data.carried_debt;
          if (res.data.total_due !== undefined) this.totalDue = res.data.total_due;
          if (res.data.reset_schedule_desc !== undefined) this.resetScheduleDesc = res.data.reset_schedule_desc;
          if (res.data.visual_balance !== undefined) this.visualBalance = res.data.visual_balance;
        }
      } catch (e) {
        console.error('Error loading wallet:', e);
      }
    },
    async onMonthChange() {
      await Promise.all([
        this.loadCalculation(),
        this.loadWallet()
      ]);
    },
    formatMonthLabel(m) {
      if (!m) return '';
      const [y, mo] = m.split('-');
      const months = ['Január', 'Február', 'Március', 'Április', 'Május', 'Június', 'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'];
      const idx = parseInt(mo, 10) - 1;
      return `${y}. ${months[idx] || mo}`;
    },
    async loadLedger() {
      try {
        const host = this.$BACKEND_HOST || '';
        const res = await axios.get(host + `/api/billing/ledger/${this.activeChildId}`);
        this.ledger = res.data.ledger || (Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Error loading ledger:', e);
      }
    },
    async loadPlans() {
      try {
        const host = this.$BACKEND_HOST || '';
        const res = await axios.get(host + `/api/billing/plans/${this.activeChildId}`);
        this.plans = res.data.plans || (Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Error loading plans:', e);
      }
    },
    async loadAbsences() {
      try {
        const host = this.$BACKEND_HOST || '';
        const res = await axios.get(host + `/api/billing/absences/${this.activeChildId}`);
        this.absences = res.data.absences || (Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Error loading absences:', e);
      }
    },
    async loadCalculation() {
      if (!this.activeChildId) return;
      try {
        const host = this.$BACKEND_HOST || '';
        const res = await axios.get(host + `/api/billing/calculate/${this.activeChildId}`, {
          params: { month: this.selectedMonth }
        });
        this.calculation = res.data.calculation || res.data;
        if (res.data && res.data.override_price !== undefined) {
          this.overridePrice = res.data.override_price;
        } else if (this.calculation && this.calculation.override_price !== undefined) {
          this.overridePrice = this.calculation.override_price;
        }
        if (res.data) {
          if (res.data.monthly_paid !== undefined) this.monthlyPaid = res.data.monthly_paid;
          if (res.data.monthly_fee_paid !== undefined) this.monthlyFeePaid = res.data.monthly_fee_paid;
          if (res.data.monthly_status !== undefined) this.monthlyStatus = res.data.monthly_status;
          if (res.data.carried_debt !== undefined) this.carriedDebt = res.data.carried_debt;
          if (res.data.total_due !== undefined) this.totalDue = res.data.total_due;
          if (res.data.reset_schedule_desc !== undefined) this.resetScheduleDesc = res.data.reset_schedule_desc;
        }
      } catch (e) {
        console.error('Error loading calculation:', e);
      }
    },
    groupName(gid) {
      const g = (this.groups || []).find(item => item.id === gid);
      return g ? g.name : `Csoport #${gid}`;
    },
    formatPrice(val) {
      if (!val && val !== 0) return '0 Ft';
      return Math.round(val).toLocaleString('hu-HU') + ' Ft';
    },
    formatHuf(val) {
      return this.formatPrice(val);
    },
    formatDateTime(dt) {
      if (!dt) return '-';
      return new Date(dt).toLocaleString('hu-HU');
    },
    getPlanFullAmount(cp) {
      if (!cp) return 0;
      if (this.overridePrice != null && this.calculation && this.calculation.plans && this.calculation.plans.length === 1) {
        return this.overridePrice;
      }
      if (cp.catalog_base > 0) return cp.catalog_base;
      return cp.base_price || 0;
    },
    isLessonDeduction(e) {
      if (!e) return false;
      if (e.txn_type !== 'deduction') return false;
      if (e.reason === 'lesson' || e.reason === 'class' || e.reason === 'monthly') return true;
      if (e.note && (e.note.toLowerCase().includes('óra') || e.note.toLowerCase().includes('lesson'))) return true;
      return false;
    },
    openTxnDetails(e) {
      this.selectedTxn = e;
      this.showTxnModal = true;
    },
    getTxnExplanation(e) {
      if (!e) return '';
      if (this.isLessonDeduction(e)) {
        return 'Ez az összeg egy megtartott és lezárt táncóra utáni arányos óradíj levonás (1 óra díja a havi tagdíj vagy az óradíj alapján számolva).';
      }
      if (e.txn_type === 'topup') {
        return 'Kredit egyenleg feltöltés vagy tagdíj befizetés rögzítése.';
      }
      if (e.txn_type === 'correction') {
        return 'Adminisztrátori egyenleg-korrekció.';
      }
      if (e.txn_type === 'refund') {
        return 'Kredit visszatérítés vagy kifizetés a táncos részére.';
      }
      return 'Egyenleg tranzakció.';
    },
    txnText(t) {
      const map = {
        topup: 'Feltöltés',
        deduction: 'Levonás / Tagdíj',
        correction: 'Korrekció',
        refund: 'Visszatérítés'
      };
      return map[t] || t;
    },
    getTxnBadgeClass(t) {
      if (t === 'topup') return 'bg-success text-white';
      if (t === 'deduction') return 'bg-primary text-white';
      if (t === 'refund') return 'bg-warning text-dark';
      return 'bg-secondary text-white';
    },
    absenceKindText(k) {
      const map = {
        proportional: 'Arányos',
        fixed: 'Fix levonás',
        percent: 'Százalék'
      };
      return map[k] || k;
    },
    openTopUp() {
      this.walletAction = { type: 'topup', title: 'Egyenleg Feltöltése' };
      const primaryGrp = (this.activeChildGroups || []).find(g => g.is_primary) || (this.activeChildGroups || [])[0];
      this.walletForm = { 
        amount: 10000, 
        note: '', 
        method: 'készpénz',
        group_id: primaryGrp ? (primaryGrp.group_id || primaryGrp.id) : 0
      };
      this.showWalletModal = true;
    },
    openCorrect() {
      this.walletAction = { type: 'correction', title: 'Egyenleg Korrekció' };
      this.walletForm = { amount: 0, note: '', method: 'készpénz', group_id: 0 };
      this.showWalletModal = true;
    },
    openRefund() {
      this.walletAction = { type: 'refund', title: 'Visszatérítés Utalás' };
      this.walletForm = { amount: 0, note: '', method: 'készpénz', group_id: 0 };
      this.showWalletModal = true;
    },
    async submitWalletAction() {
      if (!this.activeChildId || !this.walletForm.amount) return;
      try {
        const host = this.$BACKEND_HOST || '';
        const endpoint = `/api/billing/wallet/${this.walletAction.type}`;
        const payload = {
          child_id: this.activeChildId,
          amount: Number(this.walletForm.amount),
          note: this.walletForm.note,
          method: this.walletForm.method
        };
        if (this.walletAction.type === 'topup') {
          payload.group_id = this.walletForm.group_id ? Number(this.walletForm.group_id) : 0;
        }
        await axios.post(host + endpoint, payload);
        this.showWalletModal = false;
        await this.loadWallet();
        await this.loadLedger();
        if (this.walletAction.type === 'topup') {
          await this.loadChildInfo();
          await this.loadCalculation();
        }
        this.showToast('Tranzakció sikeresen rögzítve!', 'success');
      } catch (e) {
        this.showToast('Hiba a tranzakció mentése során: ' + (e.response?.data?.error || e.message), 'error');
      }
    },
    openPlanModal() {
      this.editingPlan = null;
      this.planForm = { group_id: this.unassignedGroups.length ? this.unassignedGroups[0].id : null, billing_mode: 'monthly', note: '', is_active: true };
      this.showPlanModal = true;
    },
    editPlan(plan) {
      this.editingPlan = plan;
      this.planForm = {
        group_id: plan.group_id,
        billing_mode: plan.billing_mode,
        note: plan.note || '',
        is_active: plan.is_active
      };
      this.showPlanModal = true;
    },
    async submitPlan() {
      if (!this.activeChildId) return;
      try {
        const host = this.$BACKEND_HOST || '';
        const payload = {
          child_id: this.activeChildId,
          group_id: this.planForm.group_id,
          billing_mode: this.planForm.billing_mode,
          note: this.planForm.note,
          is_active: this.planForm.is_active
        };
        if (this.editingPlan) {
          await axios.put(host + `/api/billing/plans/${this.editingPlan.id}`, payload);
          this.showToast('Díjterv sikeresen módosítva!', 'success');
        } else {
          await axios.post(host + '/api/billing/plans', payload);
          this.showToast('Új díjterv sikeresen hozzáadva!', 'success');
        }
        this.showPlanModal = false;
        await this.loadPlans();
        await this.loadCalculation();
      } catch (e) {
        this.showToast('Hiba a díjterv mentése során: ' + (e.response?.data?.error || e.message), 'error');
      }
    },
    async removePlan(id) {
      if (!confirm('Biztosan törölni szeretnéd ezt a díjtervet?')) return;
      try {
        const host = this.$BACKEND_HOST || '';
        await axios.delete(host + `/api/billing/plans/${id}`);
        this.showToast('Díjterv sikeresen törölve!', 'success');
        await this.loadPlans();
        await this.loadCalculation();
      } catch (e) {
        this.showToast('Hiba a törlés során: ' + (e.response?.data?.error || e.message), 'error');
      }
    },
    openAbsenceModal() {
      const today = new Date().toISOString().split('T')[0];
      this.absenceForm = { start_date: today, end_date: today, reduction_kind: 'proportional', reduction_value: 0.5, reason: '' };
      this.showAbsenceModal = true;
    },
    async submitAbsence() {
      if (!this.activeChildId) return;
      try {
        const host = this.$BACKEND_HOST || '';
        const payload = {
          child_id: this.activeChildId,
          start_date: this.absenceForm.start_date,
          end_date: this.absenceForm.end_date,
          reduction_kind: this.absenceForm.reduction_kind,
          reduction_value: Number(this.absenceForm.reduction_value),
          reason: this.absenceForm.reason
        };
        await axios.post(host + '/api/billing/absences', payload);
        this.showToast('Távollét sikeresen rögzítve!', 'success');
        this.showAbsenceModal = false;
        await this.loadAbsences();
        await this.loadCalculation();
      } catch (e) {
        this.showToast('Hiba a távollét mentése során: ' + (e.response?.data?.error || e.message), 'error');
      }
    },
    async removeAbsence(id) {
      if (!confirm('Biztosan törlöd ezt a távollétet?')) return;
      try {
        const host = this.$BACKEND_HOST || '';
        await axios.delete(host + `/api/billing/absences/${id}`);
        this.showToast('Távollét sikeresen törölve!', 'success');
        await this.loadAbsences();
        await this.loadCalculation();
      } catch (e) {
        this.showToast('Hiba a törlés során: ' + (e.response?.data?.error || e.message), 'error');
      }
    }
  }
};
</script>

<style scoped>
/* Page Layout */
.billing-page-container {
  max-width: 1380px;
  margin: 0 auto;
  padding: 1.75rem 1.25rem 3rem 1.25rem;
  font-family: inherit;
}

/* Hero Banner */
.stats-hero {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #0ea5e9 100%);
  border-radius: 20px;
  padding: 1.75rem 2rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  box-shadow: 0 10px 25px -5px rgba(37, 99, 235, 0.35);
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;
}
.stats-hero::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.25) 0%, rgba(255,255,255,0) 70%);
  pointer-events: none;
}

.hero-content {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  z-index: 1;
}

.hero-icon-badge {
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  flex-shrink: 0;
}

.hero-title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 0.25rem 0;
  color: #ffffff;
  letter-spacing: -0.3px;
}

.hero-subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.4;
}

.hero-child-select-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 1;
}

.hero-select-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
}

.hero-select {
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  color: #ffffff;
  font-weight: 700;
  font-size: 0.875rem;
  padding: 0.5rem 1.2rem;
  outline: none;
}
.hero-select option {
  color: var(--color-heading, #0f172a);
  background: var(--card-bg, #ffffff);
}

/* Active Dancer Info Card */
.active-dancer-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 16px;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
}

.dancer-profile-head {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.dancer-avatar-badge {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: linear-gradient(135deg, #2563eb, #1d4ed8);
  color: #ffffff;
  font-weight: 800;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dancer-main-name {
  font-size: 1.15rem;
  font-weight: 800;
  margin: 0 0 0.15rem 0;
  color: var(--color-heading, #0f172a);
}

.dancer-id-pill {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-muted, #64748b);
  background: var(--bg-secondary, #f1f5f9);
  padding: 0.15rem 0.5rem;
  border-radius: 6px;
}

.dancer-groups-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.group-pill-tag {
  background: var(--bg-secondary, #f1f5f9);
  color: var(--color-text, #334155);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 20px;
  padding: 0.35rem 0.85rem;
  font-size: 0.78rem;
  font-weight: 600;
}
.group-pill-tag.primary {
  background: rgba(37, 99, 235, 0.15);
  color: #3b82f6;
  border-color: rgba(37, 99, 235, 0.3);
  font-weight: 700;
}

.no-groups-pill {
  font-size: 0.78rem;
  color: #94a3b8;
}

/* Wallet Dashboard Card */
.wallet-dashboard-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 18px;
  padding: 1.5rem 1.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.5rem;
  box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.04);
}
.wallet-dashboard-card.in-debt {
  border-left: 5px solid #f43f5e;
}

.wallet-badge-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  display: block;
  margin-bottom: 0.25rem;
}

.wallet-amount-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.wallet-amount-val {
  font-size: 2.2rem;
  font-weight: 800;
  color: var(--color-heading, #0f172a);
  letter-spacing: -0.5px;
}

.wallet-curr-tag {
  font-size: 0.9rem;
  font-weight: 700;
  color: #64748b;
}

.wallet-status-tag {
  font-size: 0.8rem;
  font-weight: 700;
  margin-top: 0.35rem;
}
.wallet-status-tag.ok { color: #10b981; }
.wallet-status-tag.debt { color: #f43f5e; }

.wallet-actions-group {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  flex-wrap: wrap;
}

.wallet-btn {
  border: none;
  border-radius: 30px;
  padding: 0.55rem 1.2rem;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.2s ease;
}
.topup-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}
.topup-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
}
.correct-btn {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}
.correct-btn:hover { background: #2563eb; color: #ffffff; }
.refund-btn {
  background: #fff1f2;
  color: #e11d48;
  border: 1px solid #fecdd3;
}
.refund-btn:hover { background: #e11d48; color: #ffffff; }

/* Panel Cards Section */
.panel-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.03);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.panel-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.panel-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bg-blue-subtle { background: #eff6ff; }
.bg-indigo-subtle { background: #e0e7ff; }
.bg-emerald-subtle { background: #ecfdf5; }
.text-blue { color: #2563eb !important; }
.text-indigo { color: #4f46e5 !important; }
.text-emerald { color: #10b981 !important; }
.text-rose { color: #f43f5e !important; }

.panel-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--color-heading, #0f172a);
  margin: 0 0 0.15rem 0;
  letter-spacing: -0.2px;
}

.panel-subtitle {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
}

.modern-month-input {
  border-radius: 12px;
  border: 1px solid var(--color-border, #cbd5e1);
  background-color: var(--card-bg, #ffffff);
  color: var(--color-text, #0f172a);
  font-size: 0.85rem;
  font-weight: 700;
  padding: 0.4rem 0.85rem;
}

/* Monthly Payment Focus Hero Card */
.monthly-status-hero-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.25rem;
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  border: 1px solid var(--color-border, #e2e8f0);
  background: var(--card-bg, #ffffff);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
}

.monthly-status-hero-card.status-paid {
  border-left: 5px solid #10b981;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.06) 0%, var(--card-bg, #ffffff) 100%);
}

.monthly-status-hero-card.status-partial {
  border-left: 5px solid #f59e0b;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.06) 0%, var(--card-bg, #ffffff) 100%);
}

.monthly-status-hero-card.status-unpaid {
  border-left: 5px solid #ef4444;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.06) 0%, var(--card-bg, #ffffff) 100%);
}

.status-hero-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  min-width: 0;
}

.status-hero-icon-box {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.status-paid .status-hero-icon-box {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

.status-partial .status-hero-icon-box {
  background: rgba(245, 158, 11, 0.15);
  color: #f59e0b;
}

.status-unpaid .status-hero-icon-box {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.status-hero-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--color-heading, #0f172a);
}

.status-month-badge {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
  border-radius: 20px;
  background: var(--bg-secondary, #f1f5f9);
  color: var(--color-text-muted, #475569);
  border: 1px solid var(--color-border, #e2e8f0);
}

.status-hero-desc {
  font-size: 0.88rem;
  color: var(--color-text-muted, #64748b);
  margin-top: 0.25rem;
  line-height: 1.45;
}

.status-hero-right {
  flex-shrink: 0;
}

.status-paid-amount-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-text-muted, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-paid-amount-val {
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1.2;
  margin-top: 0.15rem;
}

.reset-schedule-hint {
  font-size: 0.72rem;
  color: var(--color-text-muted, #64748b);
  margin-top: 0.35rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

/* Carried Debt Banner */
.carried-debt-banner {
  background: rgba(239, 68, 68, 0.06);
  border: 1.5px solid rgba(239, 68, 68, 0.25);
  border-left: 5px solid #ef4444;
}

.carried-debt-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Calculation Breakdown Card */
.calc-breakdown-card {
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 14px;
  padding: 1.1rem 1.4rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.calc-row-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

/* User requirement: Teljes havi alapdíj is not so big */
.calc-row-item.base,
.calc-row-item.reduced-base {
  opacity: 0.85;
}

.calc-row-item.base .calc-label,
.calc-row-item.reduced-base .calc-label {
  font-size: 0.72rem;
  color: var(--color-text-muted, #64748b);
  font-weight: 600;
}

.calc-row-item.base .calc-val,
.calc-row-item.reduced-base .calc-val {
  font-size: 0.95rem !important;
  font-weight: 600 !important;
  color: var(--color-text-muted, #475569) !important;
}

.status-focus-item {
  justify-content: center;
}

.calc-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 700;
  width: fit-content;
}

.calc-status-badge.badge-paid {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.3);
}

.calc-status-badge.badge-partial {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
  border: 1px solid rgba(245, 158, 11, 0.3);
}

.calc-status-badge.badge-unpaid {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
  border: 1px solid rgba(239, 68, 68, 0.3);
}

.calc-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
}

.calc-val {
  font-size: 1.1rem;
}
.total-val {
  font-size: 1.3rem;
  font-weight: 800;
}

.plan-calc-box {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 10px;
  padding: 0.85rem 1.1rem;
}

.plans-compact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 0.5rem;
}

.plan-compact-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.45rem 0.75rem;
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 10px;
  gap: 0.5rem;
  transition: all 0.15s ease;
}

.plan-compact-item:hover {
  border-color: var(--primary-blue, #2563eb);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.plan-info-main {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
  flex: 1;
}

.plan-name {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--color-heading, #1e293b);
  max-width: 140px;
}

.mode-tag-pill {
  background: var(--bg-secondary, #f1f5f9);
  color: var(--color-text-muted, #475569);
  border-radius: 12px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}

.mode-tag-pill.mode-monthly {
  background: rgba(37, 99, 235, 0.1);
  color: var(--primary-blue, #2563eb);
}

.mode-tag-pill.mode-hourly {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

.plan-compact-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}

.btn-plan-action {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.22rem 0.5rem;
  font-size: 0.73rem;
  font-weight: 600;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s ease;
  line-height: 1;
  text-decoration: none;
}

.btn-plan-edit {
  color: var(--primary-blue, #2563eb);
  background: rgba(37, 99, 235, 0.08);
}

.btn-plan-edit:hover {
  background: var(--primary-blue, #2563eb);
  color: #ffffff;
}

.btn-plan-delete {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
}

.btn-plan-delete:hover {
  background: #ef4444;
  color: #ffffff;
}

[data-theme="dark"] .plan-compact-item {
  background: var(--gray-800, #1e293b);
  border-color: var(--gray-700, #334155);
}

[data-theme="dark"] .plan-name {
  color: var(--gray-100, #f1f5f9);
}

[data-theme="dark"] .mode-tag-pill.mode-monthly {
  background: rgba(37, 99, 235, 0.2);
  color: #93c5fd;
}

[data-theme="dark"] .mode-tag-pill.mode-hourly {
  background: rgba(245, 158, 11, 0.2);
  color: #fcd34d;
}

[data-theme="dark"] .btn-plan-edit {
  background: rgba(37, 99, 235, 0.2);
  color: #93c5fd;
}

[data-theme="dark"] .btn-plan-edit:hover {
  background: var(--primary-blue, #2563eb);
  color: #ffffff;
}

[data-theme="dark"] .btn-plan-delete {
  background: rgba(239, 68, 68, 0.2);
  color: #fca5a5;
}

[data-theme="dark"] .btn-plan-delete:hover {
  background: #ef4444;
  color: #ffffff;
}

/* Discount Chips */
.discount-chip {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
}

[data-theme="dark"] .discount-chip {
  background: rgba(16, 185, 129, 0.18);
  color: #34d399;
  border-color: rgba(16, 185, 129, 0.3);
}

.chip-x {
  background: transparent;
  border: none;
  color: #047857;
  font-weight: 800;
  font-size: 0.9rem;
  cursor: pointer;
  line-height: 1;
}

/* Modern Tables */
.table-container {
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 14px;
  overflow: hidden;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.modern-table thead th {
  background: var(--bg-secondary, #f8fafc);
  font-size: 0.73rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-muted, #64748b);
  padding: 0.9rem 1.15rem;
  border-bottom: 2px solid var(--color-border, #e2e8f0);
}

.modern-table tbody tr {
  border-bottom: 1px solid var(--color-border, #f1f5f9);
  transition: background-color 0.15s ease;
}
.modern-table tbody tr:last-child {
  border-bottom: none;
}
.modern-table tbody tr:hover {
  background-color: rgba(37, 99, 235, 0.035);
}

.modern-table tbody td {
  padding: 0.9rem 1.15rem;
  font-size: 0.88rem;
  vertical-align: middle;
  color: var(--color-text, #0f172a);
}

.empty-state {
  color: #94a3b8;
  font-size: 0.85rem;
  font-weight: 500;
}

/* Modal Styling */
.modal-backdrop-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 20px;
  width: 90%;
  max-width: 480px;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.25);
  color: var(--color-text);
}

.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  border-bottom: 1px solid var(--color-border, #f1f5f9);
  padding-bottom: 0.75rem;
}

.modal-head h3 {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--color-heading, #0f172a);
  margin: 0;
}

.close-x {
  background: transparent;
  border: none;
  font-size: 1.4rem;
  color: #94a3b8;
  cursor: pointer;
}

/* Discount Leader Card */
.discount-leader-card {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.03) 100%);
  border: 1px solid rgba(16, 185, 129, 0.25) !important;
  transition: all 0.2s ease;
}

.discount-leader-card.default-mode {
  background: var(--color-background-soft);
  border: 1px solid var(--color-border) !important;
}

.discount-leader-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
  flex-shrink: 0;
}

.discount-status-badge {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
  border: 1px solid rgba(16, 185, 129, 0.25);
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.discount-effective-label {
  color: var(--color-heading);
}

.btn-discount-link {
  background: transparent;
  border: 1.5px solid var(--primary-blue);
  color: var(--primary-blue);
  border-radius: 9999px;
  padding: 0.35rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.btn-discount-link:hover {
  background: var(--primary-blue);
  color: #ffffff;
}

.btn-discount-link-subtle {
  background: transparent;
  border: 1.5px solid var(--color-border);
  color: var(--color-text-muted);
  border-radius: 9999px;
  padding: 0.35rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.btn-discount-link-subtle:hover {
  border-color: var(--color-text-muted);
  color: var(--color-heading);
  background: var(--hover-bg);
}

/* Prorated Details Collapsible Panel */
.btn-prorated-toggle {
  background: transparent;
  border: 1.5px solid var(--primary-blue);
  color: var(--primary-blue);
  border-radius: 9999px;
  padding: 0.45rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
}

.btn-prorated-toggle:hover {
  background: rgba(37, 99, 235, 0.08);
  color: var(--primary-blue);
}

.prorated-lessons-badge {
  background: rgba(37, 99, 235, 0.1);
  color: var(--primary-blue);
  border: 1px solid rgba(37, 99, 235, 0.2);
  border-radius: 9999px;
  padding: 0.15rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 700;
}

.prorated-details-panel {
  background: linear-gradient(135deg, rgba(37, 99, 235, 0.04) 0%, rgba(99, 102, 241, 0.02) 100%);
  border-color: rgba(37, 99, 235, 0.22) !important;
  animation: fadeInDown 0.2s ease-out;
}

.prorated-header {
  border-bottom: 1px solid var(--color-border);
}

.prorated-admin-badge {
  background: rgba(37, 99, 235, 0.1);
  color: var(--primary-blue);
  border: 1px solid rgba(37, 99, 235, 0.2);
  padding: 0.3rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.prorated-expl-box {
  background: rgba(37, 99, 235, 0.05);
  border: 1px solid rgba(37, 99, 235, 0.18);
  color: var(--color-text);
  font-size: 0.85rem;
  line-height: 1.5;
}

.prorated-expl-text strong {
  color: var(--color-heading);
}

.prorated-plan-row {
  background: var(--card-bg);
  border: 1px solid var(--color-border);
  transition: all 0.15s ease;
}

.prorated-plan-row:hover {
  border-color: var(--primary-blue);
  box-shadow: var(--shadow-sm);
}

.prorated-group-name {
  font-weight: 700;
  color: var(--color-heading);
  font-size: 0.95rem;
}

.prorated-plan-amount {
  font-weight: 700;
  color: var(--primary-blue);
  font-size: 1rem;
}

.prorated-meta {
  color: var(--color-text-muted);
}

.prorated-meta strong {
  color: var(--color-heading);
}

/* Absences */
.absence-row-item {
  background: var(--color-background-soft);
  border: 1px solid var(--color-border);
  color: var(--color-text);
}

.absence-dates {
  color: var(--color-heading);
}

/* Wallet Double Check Dancer Card */
.verify-dancer-card {
  background: linear-gradient(135deg, rgba(79, 70, 229, 0.08), rgba(99, 102, 241, 0.04));
  border: 1px solid rgba(79, 70, 229, 0.2);
}

.verify-dancer-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--color-text-muted);
}

.verify-dancer-id {
  background: var(--primary-blue);
  color: white;
  border-radius: 9999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.72rem;
  font-weight: 700;
}

.verify-dancer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(79, 70, 229, 0.1);
  color: #4f46e5;
  font-weight: 700;
  font-size: 1.05rem;
  border: 1px solid rgba(79, 70, 229, 0.25);
  flex-shrink: 0;
}

.verify-dancer-name {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--color-heading);
}

.badge-group-selected {
  background: var(--color-background-soft);
  color: var(--color-text-muted);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  padding: 0.2rem 0.5rem;
  font-size: 0.75rem;
}

/* Transaction Details Modal */
.txn-summary-box {
  background: var(--bg-secondary, #f8fafc);
  border: 1px solid var(--color-border);
}

.txn-val-text {
  color: var(--color-heading);
}

.txn-explanation-box {
  background: rgba(37, 99, 235, 0.08);
  border: 1px solid rgba(37, 99, 235, 0.25);
  color: var(--color-text);
}

.txn-explanation-title {
  font-weight: 700;
  color: var(--primary-blue);
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ledger-table-row {
  cursor: pointer;
}

.ledger-table-row:hover {
  background-color: rgba(37, 99, 235, 0.04) !important;
}

/* Dark Theme Overrides for BillingItem */
[data-theme="dark"] .discount-leader-card {
  background: rgba(16, 185, 129, 0.1) !important;
  border-color: rgba(16, 185, 129, 0.25) !important;
}

[data-theme="dark"] .discount-leader-card.default-mode {
  background: var(--gray-800) !important;
  border-color: var(--color-border) !important;
}

[data-theme="dark"] .discount-leader-icon {
  background: rgba(16, 185, 129, 0.2);
  color: #34d399;
}

[data-theme="dark"] .discount-status-badge {
  background: rgba(16, 185, 129, 0.2);
  color: #6ee7b7;
  border-color: rgba(16, 185, 129, 0.4);
}

[data-theme="dark"] .btn-discount-link {
  border-color: #60a5fa;
  color: #93c5fd;
}

[data-theme="dark"] .btn-discount-link:hover {
  background: #2563eb;
  color: #ffffff;
}

[data-theme="dark"] .btn-prorated-toggle {
  border-color: #60a5fa;
  color: #93c5fd;
}

[data-theme="dark"] .btn-prorated-toggle:hover {
  background: rgba(59, 130, 246, 0.15);
  color: #bfdbfe;
}

[data-theme="dark"] .prorated-lessons-badge,
[data-theme="dark"] .prorated-admin-badge {
  background: rgba(37, 99, 235, 0.25);
  color: #93c5fd;
  border-color: rgba(37, 99, 235, 0.4);
}

[data-theme="dark"] .prorated-details-panel {
  background: rgba(30, 41, 59, 0.7);
  border-color: rgba(99, 102, 241, 0.3) !important;
}

[data-theme="dark"] .prorated-expl-box {
  background: rgba(37, 99, 235, 0.12);
  border-color: rgba(37, 99, 235, 0.28);
  color: var(--color-text);
}

[data-theme="dark"] .prorated-plan-row {
  background: var(--gray-800);
  border-color: var(--color-border);
}

[data-theme="dark"] .prorated-plan-amount {
  color: #60a5fa;
}

[data-theme="dark"] .absence-row-item {
  background: var(--gray-800);
  border-color: var(--color-border);
}

[data-theme="dark"] .verify-dancer-card {
  background: rgba(99, 102, 241, 0.12);
  border-color: rgba(99, 102, 241, 0.3);
}

[data-theme="dark"] .verify-dancer-avatar {
  background: rgba(99, 102, 241, 0.25);
  color: #a5b4fc;
  border-color: rgba(99, 102, 241, 0.4);
}

[data-theme="dark"] .badge-group-selected {
  background: var(--gray-800);
  border-color: var(--color-border);
  color: var(--color-text-muted);
}

[data-theme="dark"] .txn-summary-box {
  background: var(--gray-800);
  border-color: var(--color-border);
}

[data-theme="dark"] .txn-explanation-box {
  background: rgba(37, 99, 235, 0.16);
  border-color: rgba(37, 99, 235, 0.35);
  color: var(--color-text);
}

[data-theme="dark"] .txn-explanation-title {
  color: #93c5fd;
}
</style>
